import { Injectable } from "@angular/core";
import { BehaviorSubject } from "rxjs";

export interface ToastAction {
  label: string;
  callback: () => void;
}

export interface Toast {
  id: string;
  type: 'success' | 'error' | 'warning' | 'info';
  title: string;
  message: string;
  duration?: number; // in milliseconds, 0 for persistent
  action?: ToastAction; // Optional action button
}

@Injectable({
  providedIn: "root",
})
export class ToastService {
  private toastsSubject = new BehaviorSubject<Toast[]>([]);
  public toasts$ = this.toastsSubject.asObservable();

  private toasts: Toast[] = [];

  constructor() {}

  /**
   * Show a success toast notification
   */
  showSuccess(title: string, message: string, duration: number = 5000, action?: ToastAction): void {
    this.addToast({
      id: this.generateId(),
      type: 'success',
      title,
      message,
      duration,
      action
    });
  }

  /**
   * Show an error toast notification
   */
  showError(title: string, message: string, duration: number = 0): void {
    this.addToast({
      id: this.generateId(),
      type: 'error',
      title,
      message,
      duration
    });
  }

  /**
   * Show a warning toast notification
   */
  showWarning(title: string, message: string, duration: number = 5000): void {
    this.addToast({
      id: this.generateId(),
      type: 'warning',
      title,
      message,
      duration
    });
  }

  /**
   * Show an info toast notification
   */
  showInfo(title: string, message: string, duration: number = 5000): void {
    this.addToast({
      id: this.generateId(),
      type: 'info',
      title,
      message,
      duration
    });
  }

  /**
   * Remove a toast by ID
   */
  removeToast(id: string): void {
    this.toasts = this.toasts.filter(toast => toast.id !== id);
    this.toastsSubject.next([...this.toasts]);
  }

  /**
   * Clear all toasts
   */
  clearAll(): void {
    this.toasts = [];
    this.toastsSubject.next([]);
  }

  private addToast(toast: Toast): void {
    this.toasts.push(toast);
    this.toastsSubject.next([...this.toasts]);

    // Auto-remove toast after duration if specified
    if (toast.duration && toast.duration > 0) {
      setTimeout(() => {
        this.removeToast(toast.id);
      }, toast.duration);
    }
  }

  private generateId(): string {
    return 'toast-' + Math.random().toString(36).substr(2, 9) + '-' + Date.now();
  }
}